import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Package } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
// 경계 규칙(Component→Hook→Service) 명시적 예외: 마운트 즉시 무조건 fetch하는 훅은 '미해제일
// 때만 조회' 게이트와 충돌 — 1회성 팝업 하나를 위한 조건부 fetch 훅 신설 대신 service 함수를
// 직접 호출한다 (repository/클라이언트 직접 접근은 아님, DepositLaunchModal 선례).
import { hasAnyInventoryItemEver } from "@/supabase/inventory/service/inventoryService";
import { hasOrdersSince } from "@/supabase/order/service/orderService";
import { INVENTORY_LAUNCH_ANNOUNCEMENT } from "@/lib/inventoryLaunch";
import { subDays } from "date-fns";

const STORAGE_KEY = "inventory_launch_notice_dismissed";
// DepositLaunchModal의 STORAGE_KEY 리터럴 복제 — 두 출시 공지가 같은 세션에 겹쳐 뜨는 것을
// 막기 위한 직렬화 게이트에 쓴다(아래 effect). donor는 출시 완료된 파일이라 export 추가조차
// 하지 않고 리터럴을 미러한다(값이 바뀔 일 없는 영구 키 — 바뀌면 기존 유저 해제가 풀리므로
// donor 쪽도 불변 계약).
const DEPOSIT_NOTICE_STORAGE_KEY = "deposit_launch_notice_dismissed";
// 타겟팅: 최근 30일 내 정산서(order) 사용 이력이 있는 유저에게만 노출(오너 2026-07-17 —
// 재고관리는 정산서 연동이 핵심 가치라 정산서 사용자로 좁힌다. donor(deposit)의
// parsed_order OR 조건은 제거 — 자동정리만 쓰는 유저는 차감 혜택 대상이 아님).
const RECENT_USAGE_DAYS = 30;

// localStorage 접근이 차단된 환경(프라이버시 모드 등)에서 effect/CTA가 죽지 않도록 가드.
function safeGetDismissed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== null;
  } catch {
    // 저장소를 못 읽으면 노출하지 않는 쪽이 안전 — '다시 보지 않기'도 저장 불가라 재노출 루프가 되기 때문.
    return true;
  }
}

function safeSetDismissed() {
  try {
    localStorage.setItem(STORAGE_KEY, "true");
  } catch {
    // 저장 실패는 무시 — 이번 세션의 노출 억제는 isOpen state가 담당.
  }
}

// 기능 칩은 이 모달 전용 카피 — deposit의 은행 칩(banks.ts 단일 소스 파생)과 달리
// 파생할 단일 소스가 없어 정적 리터럴로 고정한다.
const FEATURE_CHIPS = ["입금 완료 시 자동 차감", "정산서 화면 내 상품 + 재고 뱃지", "엑셀 일괄 등록"];

export function InventoryLaunchModal() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    // 런칭 플래그 최우선 동기 게이트 — false인 동안 localStorage 접근·서비스 호출·모달 렌더 전부 0.
    // "머지 ≠ 런칭": 이 컴포넌트가 머지돼도 플래그 flip 전엔 어떤 유저에게도 아무 일도 일어나지 않는다.
    if (!INVENTORY_LAUNCH_ANNOUNCEMENT) return;
    // 로그인 + 미해제 상태에서만 검사. dep은 userId — user 객체 dep이면 토큰 리프레시마다
    // 재실행되어 '닫기' 후 같은 세션에서 재노출됨 (닫기=다음 접속 시 재노출 계약 위반).
    if (!userId) return;
    if (safeGetDismissed()) return;
    // 출시 공지 직렬화(codexloop PR7-3 R1 P1): 입금매칭 공지를 아직 영구 해제하지 않은 유저는
    // 이번 세션 보류 — 두 모달이 같은 z-index 포털에 겹쳐 뜨고 포커스 트랩이 경쟁하는 것을
    // 막는다. deposit 공지가 먼저(선출시), 그 키가 세션마다 '닫기'만 되는 유저에겐 이 공지가
    // 계속 보류되지만, 그런 유저는 deposit 공지를 매 세션 보고 있는 중이라 공지 추가가 오히려
    // 소음이다(수용된 트레이드오프).
    try {
      if (localStorage.getItem(DEPOSIT_NOTICE_STORAGE_KEY) === null) return;
    } catch {
      return; // 저장소를 못 읽으면 safeGetDismissed와 동일하게 보류가 안전.
    }
    // 이미 /inventory에 있는 유저에겐 이중 소개하지 않는다 — 페이지 자체가 같은 역할.
    // (트레일링 슬래시 정규화: React Router는 /inventory/ 도 같은 라우트로 매칭)
    if (window.location.pathname.replace(/\/+$/, "") === "/inventory") return;

    let cancelled = false;
    void (async () => {
      // 사용 이력 게이트: 최근 30일 내 정산서 이력 없는 유저는 이번 세션 노출 보류.
      // 영구 해제가 아님 — 나중에 기능을 쓰기 시작하면 그때 노출된다. 판별 실패 시에도 보류(오타겟 방지).
      try {
        // created_at 기준 — order_date는 수정 가능한 영업일자라 활동 판별에 부적합
        const sinceIso = subDays(new Date(), RECENT_USAGE_DAYS).toISOString();
        const isActiveUser = await hasOrdersSince(userId, sinceIso);
        if (cancelled || !isActiveUser) return;
      } catch {
        return;
      }

      try {
        // 소프트 삭제 포함 존재 판정 — "지금 상품이 있는가"가 아니라 "써 본 적 있는가"(베타
        // 시절 등록했다 전부 지운 유저도 이미 아는 유저). 경량 id 1건 조회라 quick-add RPC의
        // 예약 집계 스캔을 세션마다 태우지 않는다(codexloop PR7-3 R1 수용).
        const alreadyUsed = await hasAnyInventoryItemEver();
        if (cancelled) return;
        if (alreadyUsed) {
          // adminOnly 시절 직접 URL로 이미 쓰던 유저에겐 소개 불필요 — 조용히 영구 해제 (이후 재조회 없음)
          safeSetDismissed();
          return;
        }
        // 비동기 게이트를 도는 사이 /inventory로 이동했을 수 있다 — 열기 직전 경로 재확인
        // (그 페이지 위에 그 페이지 소개를 띄우지 않기, codexloop PR7-3 R1 수용).
        if (window.location.pathname.replace(/\/+$/, "") === "/inventory") return;
        setIsOpen(true);
      } catch {
        // 조회 실패 = 이번 세션 보류(fail-closed) — deposit 공지의 fail-open과 달리, 재고
        // 조회가 실패하는 상황(RPC 롤아웃/권한/장애)에 그 기능을 광고하며 유입시키는 건
        // 역효과고, 한 세션 보류는 잃는 게 없다(codexloop PR7-3 R1 수용. repo가 이미 logError).
        return;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleDontShowAgain = () => {
    safeSetDismissed();
    setIsOpen(false);
  };

  const handleGoToInventory = () => {
    safeSetDismissed();
    setIsOpen(false);
    navigate("/inventory");
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent
        className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-lg"
        // 첫 포커스가 '다시 보지 않기'(DOM상 첫 버튼)에 잡혀 테두리 박스처럼 보이거나(링),
        // 하단 CTA 포커스로 overflow 상태에서 열리자마자 아래로 스크롤되는 것 방지 —
        // 상단 제목(tabIndex=-1)에 포커스: 제목부터 보이고, Tab 시 첫 버튼에 focus-visible 링 정상 표시.
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          titleRef.current?.focus();
        }}
      >
        <DialogHeader className="space-y-3 text-left">
          {/* 시각 앵커: 아이콘 배지 + NEW — 출시 공지임을 텍스트 이전에 전달 */}
          <div className="flex items-center gap-2.5">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100">
              <Package className="h-5 w-5 text-slate-700" />
            </span>
            <span className="rounded-full bg-blue-600 px-2 py-0.5 text-[11px] font-bold tracking-wide text-white">
              NEW
            </span>
          </div>
          <DialogTitle
            ref={titleRef}
            tabIndex={-1}
            className="break-keep text-xl font-bold leading-snug text-slate-900 outline-none"
          >
            재고 관리 기능이 출시되었습니다
          </DialogTitle>
          {/* asChild: Radix Description은 <p>라 내부 <div>가 invalid nesting — div로 대체 */}
          <DialogDescription asChild>
            <div className="space-y-4 pt-1 text-left">
              {/* 하이라이트 박스("라이브 중에 재고를…")는 오너 지시로 삭제(2026-07-17),
                  '주문 화면'→'정산서 화면' 용어 통일도 동일 지시. */}
              <p className="break-keep text-[15px] leading-relaxed text-slate-600">
                <strong className="font-semibold text-slate-900">상품을 등록</strong>
                해 두면 정산서를{" "}
                <strong className="font-semibold text-slate-900">입금 완료</strong>{" "}
                처리할 때마다 재고가 자동으로 차감되고, 정산서 화면에서{" "}
                <strong className="font-semibold text-slate-900">
                  내 상품을 바로 담을
                </strong>{" "}
                수 있습니다.
              </p>
              {/* 기능 칩: 고아 단어 대신 그룹 캡션으로 승격 (deposit 은행 칩과 동일 스타일) */}
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-slate-500">주요 기능</p>
                <div className="flex flex-wrap gap-1.5">
                  {FEATURE_CHIPS.map((feature) => (
                    <span
                      key={feature}
                      className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-2 flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
          {/* tertiary: 텍스트 링크 수준 강등 — primary와 시각 경쟁 제거 */}
          <Button
            type="button"
            variant="ghost"
            onClick={handleDontShowAgain}
            className="h-auto self-center px-2 py-2 text-xs font-normal text-slate-500 hover:bg-transparent hover:text-slate-700 sm:self-auto"
          >
            다시 보지 않기
          </Button>
          <div className="flex w-full flex-col-reverse gap-2 sm:w-auto sm:flex-row">
            <Button type="button" variant="outline" onClick={handleClose} className="w-full sm:w-auto">
              닫기
            </Button>
            <Button
              type="button"
              onClick={handleGoToInventory}
              className="w-full sm:w-auto bg-slate-900 px-5 text-white hover:bg-slate-800"
            >
              재고 관리 열어보기
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
